import { useDeepCompareEffect } from '@fuse/hooks';
import _ from '@lodash';
import AppContext from 'app/AppContext';
import {
  generateSettings,
  selectFuseCurrentSettings,
  selectFuseDefaultSettings,
  setSettings,
} from 'app/store/fuse/settingsSlice';
import { memo, useCallback, useContext, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { matchRoutes, useLocation } from 'react-router-dom';
import GlobalStyles from '@mui/material/GlobalStyles';
import { alpha } from '@mui/material/styles';

function FuseLayout(props) {
  const { layouts } = props;
  const dispatch = useDispatch();
  const settings = useSelector(selectFuseCurrentSettings);
  const defaultSettings = useSelector(selectFuseDefaultSettings);

  const appContext = useContext(AppContext);
  const { routes } = appContext;

  const location = useLocation();
  const { pathname } = location;

  const matchedRoutes = matchRoutes(routes, pathname);
  const matched = matchedRoutes ? matchedRoutes[0] : false;

  const newSettings = useRef(null);

  const shouldAwaitRender = useCallback(() => {
    let _newSettings;

    if (matched && matched.route.settings) {
      const routeSettings = matched.route.settings;
      _newSettings = generateSettings(defaultSettings, routeSettings);
    } else if (!_.isEqual(newSettings.current, defaultSettings)) {
      _newSettings = _.merge({}, defaultSettings);
    } else {
      _newSettings = newSettings.current;
    }

    if (!_.isEqual(newSettings.current, _newSettings)) {
      newSettings.current = _newSettings;
    }
  }, [defaultSettings, matched]);

  shouldAwaitRender();

  useDeepCompareEffect(() => {
    if (!_.isEqual(newSettings.current, settings)) {
      dispatch(setSettings(newSettings.current));
    }
  }, [dispatch, newSettings.current, settings]);

  const Layout = useMemo(() => layouts[settings.layout.style], [layouts, settings.layout.style]);

  return _.isEqual(newSettings.current, settings) ? (
    <>
      <GlobalStyles
        styles={(theme) => ({
          html: {
            backgroundColor: `${theme.palette.background.default}!important`,
            color: `${theme.palette.text.primary}!important`,
          },
          body: {
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
          },
          'table.simple tbody tr th': {
            borderColor: theme.palette.divider,
          },
          'table.simple thead tr th': {
            borderColor: theme.palette.divider,
          },
          'a:not([role=button]):not(.MuiButtonBase-root)': {
            color: theme.palette.secondary.main,
            textDecoration: 'underline',
          },
          'a.link, a:not([role=button])[target=_blank]': {
            background: alpha(theme.palette.secondary.main, 0.2),
            color: 'inherit',
            borderBottom: `1px solid ${theme.palette.divider}`,
            textDecoration: 'none',
          },
          '::-webkit-scrollbar-thumb': {
            boxShadow: `inset 0 0 0 20px ${
              theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.24)' : 'rgba(255, 255, 255, 0.24)'
            }`,
          },
          'body .ag-theme-alpine-dark, body .ag-theme-balham-dark, body .ag-theme-alpine, body .ag-theme-balham': {
            '--ag-background-color': `${theme.palette.background.paper} !important`,
            '--ag-header-background-color': `${theme.palette.background.default} !important`,
            '--ag-foreground-color': `${theme.palette.text.primary} !important`,
            '--ag-header-foreground-color': `${theme.palette.text.primary} !important`,
            '--ag-border-color': `${theme.palette.divider} !important`,
            '--ag-row-border-color': `${theme.palette.divider} !important`,
            '--ag-odd-row-background-color': `${alpha(theme.palette.text.primary, 0.02)} !important`,
            '--ag-row-hover-color': `${alpha(theme.palette.text.primary, 0.05)} !important`,
            '--ag-accent-color': `${theme.palette.secondary.main} !important`,
            '--ag-checkbox-checked-color': `${theme.palette.secondary.main} !important`,
            '--ag-selected-row-background-color': `${alpha(theme.palette.secondary.main, 0.15)} !important`,
            '--ag-range-selection-border-color': `${theme.palette.secondary.main} !important`,
            '--ag-range-selection-background-color': `${alpha(theme.palette.secondary.main, 0.15)} !important`,
            '--ag-input-focus-border-color': `${theme.palette.secondary.main} !important`,
          },
          'body .ag-menu': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            color: `${theme.palette.text.primary} !important`,
          },
          'body .ag-menu-option': {
            color: `${theme.palette.text.primary} !important`,
          },
          'body .ag-menu-option:hover': {
            backgroundColor: `${alpha(theme.palette.secondary.main, 0.1)} !important`,
          },
          'body .ag-theme-alpine-dark .ag-header, body .ag-theme-balham-dark .ag-header': {
            borderBottom: `2px solid ${theme.palette.secondary.main} !important`,
          },
          'body .ag-theme-alpine-dark .ag-column-drop, body .ag-theme-balham-dark .ag-column-drop': {
            backgroundColor: `${theme.palette.background.default} !important`,
            borderBottom: `1px solid ${theme.palette.divider} !important`,
          },
          'body .ag-theme-alpine-dark .ag-column-drop-empty-message, body .ag-theme-balham-dark .ag-column-drop-empty-message': {
            color: `${theme.palette.text.primary} !important`,
          },
          '#fuse-toolbar': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            color: `${theme.palette.text.primary} !important`,
          },
          '#fuse-navbar': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            color: `${theme.palette.text.primary} !important`,
          },
          '#fuse-footer': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            color: `${theme.palette.text.primary} !important`,
          },
        })}
      />
      <Layout {...props} />
    </>
  ) : null;
}

export default memo(FuseLayout);